import { useContext, useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"

//PrimeReact
import { Button } from "primereact/button"
import { TabPanel, TabView } from "primereact/tabview"
import { Dropdown } from "primereact/dropdown"

//styling
import assessStyles from "../../../styles/assessments/Assessment.module.css"
import myStyles from "../../../styles/assessments/ParticipantAssessments.module.css"

// Context
import { UserContext } from "../../../context/userContext"

//GraphQL
import { useLazyQuery } from "@apollo/client"
import { GET_LATEST_SERVICE_EPISODE, GET_STAFF_LIST } from "../queries"

//Assessments components
import ParticipantAssessmentsOverview from "./ParticipantAssessmentsOverview"
import ParticipantAssessmentsCompleted from "./ParticipantAssessmentsCompleted"
import AssessmentForm from "../AssessmentForm/AssessmentForm"
import ParticipantAssessmentsSchedules from "./ParticipantAssessmentsSchedules"
import ParticipantAssessmentBlockDialog from "./ParticipantAssessmentBlockDialog"

//Helpers
import { LogError } from "../../../helpers/logger"
import isParticipantActive from "../../../helpers/isParticipantActive"
import InactiveParticipantToolTip from "./InactiveParticipantToolTip"
import AppBreadcrumb from "../../../components/AppBreadcrumb/AppBreadcrumb"
import { classNames } from "primereact/utils"
import { getAssessmentsServiceStaffId } from "../../../config/microservicesSystemUsers"

export default function ParticipantAssessments() {
  const userCtx = useContext(UserContext)
  const location = useLocation()
  const [tabViewIndex, setTabViewIndex] = useState(0)
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(userCtx.participant_organizations[0]?.organization_id)

  //Assessment block values:
  const [showAssessmentBlock, setShowAssessmentBlock] = useState(false)
  const [scheduledTaskId, setScheduledTaskId] = useState(undefined)

  //Assessment form values:
  const [newAssessmentId, setNewAssessmentId] = useState(undefined)
  const [newAssessmentButtonText, setNewAssessmentButtonText] = useState("Start new assessment")
  const [completedId, setCompletedId] = useState(undefined)
  const [openReadOnly, setOpenReadOnly] = useState(true)
  const [showAssessmentForm, setShowAssessmentForm] = useState(false)
  const [triggerRefresh, setTriggerRefresh] = useState(false)
  const [participantServiceEpisode, setParticipantServiceEpisode] = useState({
    isLoading: true,
  })
  const facilitatorProfilesRef = useRef([])
  const [isChildLoading, setIsChildLoading] = useState(true)

  // Handle screen resize in realtime
  const screenType = useRef("")
  const screenTypeLoading = useRef(false)
  useEffect(() => {
    const handleResize = () => {
      if (!screenTypeLoading.current) {
        if (window.innerWidth <= 768 && screenType.current !== "mobile") {
          screenTypeLoading.current = true
          screenType.current = "mobile"

          setNewAssessmentButtonText("New")

          screenTypeLoading.current = false
        }
        else if (window.innerWidth > 768 && screenType.current !== "normal") {
          screenTypeLoading.current = true
          screenType.current = "normal"

          setNewAssessmentButtonText("Start new assessment")

          screenTypeLoading.current = false
        }
      }
    };
    handleResize()

    window.addEventListener('resize', handleResize);
    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const participantIsActive = (organization_id) => (
    isParticipantActive(
      userCtx.participant_organizations?.find(o => o.organization_id === organization_id)?.participant_membership_status
    )
  )

  const [loadStaff] = useLazyQuery(GET_STAFF_LIST, {
    fetchPolicy: "cache-and-network",
    skip: true,
    variables: {
      staff_id: getAssessmentsServiceStaffId(), //Service staff account: RecoveryLInk Microservices
      organization_id: selectedOrganizationId,
    },
  })

  const [getParticipant] = useLazyQuery(GET_LATEST_SERVICE_EPISODE, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: "",
      participant_id: "",
      organization_id: "",
      participant_membership_id: "",
    },
  })

  useEffect(() => {
    if (location.state) {
      if (location.state.taskId) {
        //Open the Scheduled assessments tab view
        setTabViewIndex(2)

        // Set selected organization if provided in state
        if (location.state.organization_id && location.state.organization_id !== selectedOrganizationId) {
          setSelectedOrganizationId(location.state.organization_id)
        }

        //Show the task form to complete the task
        updateShowAssessmentBlock(true, location.state.taskId)

        // Clear location state to prevent re-processing on future renders
        location.state = {}
        window.history.replaceState({}, document.title)
      }
      else if (location.state.page) {
        switch (location.state.page) {
          case "schedule":
            setTabViewIndex(2)
            break

          default:
            break
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  useEffect(() => {
    if (userCtx.participant_id && selectedOrganizationId) {
      getParticipant({
        variables: {
          staff_id: getAssessmentsServiceStaffId(), //Assessments service user
          participant_id: userCtx.participant_id,
          organization_id: selectedOrganizationId,
          participant_membership_id: userCtx.participant_organizations.find(o => o.organization_id === selectedOrganizationId)?.participant_membership_id,
        },
      })
        .then((result) => {
          if (result.data) {
            setParticipantServiceEpisode(result.data)
          } else {
            LogError("Unable to retrieve service episode data", result)
            userCtx.setNotificationError(
              "Unable to retrieve service episode data"
            )
            setParticipantServiceEpisode(undefined)
          }
        })
        .catch((error) => {
          LogError("Unable to retrieve service episode data", error)
          userCtx.setNotificationError("Unable to retrieve service episode data")
          setParticipantServiceEpisode(undefined)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.participant_id, selectedOrganizationId])

  useEffect(() => {
    if (selectedOrganizationId) {
      loadStaff({
        variables: {
          staff_id: getAssessmentsServiceStaffId(), //Service staff account: RecoveryLInk Microservices
          organization_id: selectedOrganizationId,
        },
      })
        .then((result) => {
          if (result.data && result.data.users) {
            if (facilitatorProfilesRef.current) {
              let facilitatorList = result.data.users.filter(
                (user) =>
                  !facilitatorProfilesRef.current.some(
                    (facilitator) =>
                      facilitator.staff_id === user.staff_id &&
                      facilitator.organization_id === user.organizationId
                  )
              )

              facilitatorList = facilitatorList.map((user) => {
                return {
                  profile_id: user.staff_id,
                  organization_id: selectedOrganizationId,
                  name: `${user.staff_name_first} ${user.staff_name_last}`,
                }
              })

              facilitatorProfilesRef.current = [
                ...facilitatorProfilesRef.current,
                ...facilitatorList,
              ]
            } else {
              facilitatorProfilesRef.current = result.data.users.map((user) => {
                return {
                  profile_id: user.staff_id,
                  organization_id: selectedOrganizationId,
                  name: `${user.staff_name_first} ${user.staff_name_last}`,
                }
              })
            }
          } else {
            LogError("Unable to retrieve staff list", result)
            userCtx.setNotificationError("Unable to retrieve staff list")
          }
        })
        .catch((error) => {
          LogError("Unable to retrieve staff list", error)
          userCtx.setNotificationError("Unable to retrieve staff list")
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganizationId])

  const updateShowAssessmentBlock = (showBlock, showScheduledTaskId) => {
    if (showBlock) {
      setScheduledTaskId(showScheduledTaskId)
      setShowAssessmentBlock(true)
    } else {
      setShowAssessmentBlock(false)
      setScheduledTaskId(undefined)
    }
  }

  const setAssessmentId = (id, isCompletedId, readOnly) => {
    if (!isCompletedId) {
      setNewAssessmentId(id)
      setCompletedId(undefined)
      setOpenReadOnly(false)
      setShowAssessmentForm(true)
    } else {
      setNewAssessmentId(undefined)
      setCompletedId(id)
      setOpenReadOnly(readOnly || true)
      setShowAssessmentForm(true)
    }
  }

  const onSaveFacilitator = (profile_id, organization_id, name) => {
    let currentFacilitators = facilitatorProfilesRef.current

    if (
      !currentFacilitators.some(
        (profile) =>
          profile.profile_id === profile_id &&
          profile.organization_id === organization_id
      )
    ) {
      currentFacilitators = [
        ...currentFacilitators,
        { profile_id, organization_id, name },
      ]

      facilitatorProfilesRef.current = currentFacilitators
    }

    return facilitatorProfilesRef.current
  }

  const OrganizationSelector = ({ view }) => {
    return (
      <Dropdown
        value={selectedOrganizationId}
        options={userCtx.participant_organizations}
        onChange={(e => {
          // Set participantServiceEpisode.isLoading = true, 
          // because changing the selectedOrganizationId will trigger a data reload 
          setParticipantServiceEpisode({
            isLoading: true,
          })
          setSelectedOrganizationId(e.value)
        })}
        placeholder="Select an Organization"
        className={view === "mobile" ? myStyles.orgSelectorMobile : myStyles.orgSelector}
        optionLabel="organization_name"
        optionValue="organization_id"
        disabled={participantServiceEpisode?.isLoading === true || isChildLoading}
      />
    )
  }

  const getParticipantAssessmentsOverview = () => {
    return (
      <ParticipantAssessmentsOverview
        participantServiceEpisode={participantServiceEpisode}
        onViewAssessment={(completedAssessmentId) => {
          setAssessmentId(completedAssessmentId, true)
        }}
        facilitatorProfiles={facilitatorProfilesRef.current}
        onSaveFacilitatorProfile={onSaveFacilitator}
        selectedOrganizationId={selectedOrganizationId}
        setIsChildLoading={setIsChildLoading}
      />
    )
  }

  const getParticipantAssessmentsCompleted = () => {
    return (
      <ParticipantAssessmentsCompleted
        triggerRefresh={triggerRefresh}
        onEdit={(completedAssessmentId) => {
          setAssessmentId(completedAssessmentId, true, false)
        }}
        onView={(completedAssessmentId) => {
          setAssessmentId(completedAssessmentId, true, true)
        }}
        facilitatorProfiles={facilitatorProfilesRef.current}
        onSaveFacilitatorProfile={onSaveFacilitator}
        selectedOrganizationId={selectedOrganizationId}
        setIsChildLoading={setIsChildLoading}
      />
    )
  }

  const getParticipantAssessmentsSchedules = () => {
    return (
      <ParticipantAssessmentsSchedules
        triggerRefresh={triggerRefresh}
        onProcessScheduledTask={(scheduledTaskId) => {
          updateShowAssessmentBlock(true, scheduledTaskId)
        }}
        selectedOrganizationId={selectedOrganizationId}
        setIsChildLoading={setIsChildLoading}
      />
    )
  }

  return (
    <div>
      {showAssessmentBlock ? (
        <ParticipantAssessmentBlockDialog
          showForm={true}
          scheduledTaskId={scheduledTaskId}
          onCancel={() => {
            updateShowAssessmentBlock(false)
            setTriggerRefresh(!triggerRefresh)
          }}
          selectedOrganizationId={selectedOrganizationId}
        />
      ) : null}
      {showAssessmentForm ? (
        <AssessmentForm
          showForm={showAssessmentForm}
          onCancel={() => {
            setShowAssessmentForm(false)
            setCompletedId(undefined)
          }}
          onSubmit={() => {
            setShowAssessmentForm(false)
            setCompletedId(undefined)
            setTriggerRefresh(!triggerRefresh)
          }}
          assessmentId={newAssessmentId}
          completedAssessmentId={completedId}
          readOnly={openReadOnly}
          selectedOrganizationId={selectedOrganizationId}
        />
      ) : null}

      <div className="main-content">
        <AppBreadcrumb items={[{ label: "Assessments" }]} />
        <div className="flex flex-row align-items-top justify-content-between">
          <div className="flex flex-row gap-3">
            <h1>Assessments</h1>
            <OrganizationSelector view={"normal"} />
          </div>

          <div className={myStyles.pageHeaderActions}>
            <InactiveParticipantToolTip organization_id={selectedOrganizationId}>
              <Button
                label={newAssessmentButtonText}
                type="button"
                className={`${assessStyles.button} ${myStyles.pageHeaderActionButton}`}
                onClick={() => {
                  updateShowAssessmentBlock(true)
                }}
                disabled={!participantIsActive(selectedOrganizationId)}
              />
            </InactiveParticipantToolTip>
          </div>
        </div>

        <hr className="line mt-2" />

        <div className="mt-3 mb-2">
          <OrganizationSelector view={"mobile"} />
        </div>

        <div className={classNames(assessStyles.tabView, myStyles.tabView)}>
          <TabView
            activeIndex={tabViewIndex}
            onTabChange={(e) => {
              setTabViewIndex(e.index)
            }}
          >
            <TabPanel header={"Overview"}>
              {getParticipantAssessmentsOverview()}
            </TabPanel>
            <TabPanel header={"Completed"}>
              {getParticipantAssessmentsCompleted()}
            </TabPanel>
            <TabPanel header={"Scheduled"}>
              {getParticipantAssessmentsSchedules()}
            </TabPanel>
          </TabView>
        </div>

        <div className={myStyles.tabViewMobile}>
          <div className={myStyles.tabButtonContainer}>
            <Button className={myStyles.viewButton} onClick={() => setTabViewIndex(0)} >
              <div className={classNames(myStyles.viewButtonContent, tabViewIndex === 0 && myStyles.viewButtonContentSelected)}>
                <span className="material-icons">
                  dashboard
                </span>
                <div>
                  Overview
                </div>
              </div>
            </Button>
            <Button className={myStyles.viewButton} onClick={() => setTabViewIndex(1)}>
              <div className={classNames(myStyles.viewButtonContent, tabViewIndex === 1 && myStyles.viewButtonContentSelected)}>
                <span className="material-icons">
                  assignment_turned_in
                </span>
                <div>
                  Completed
                </div>
              </div>
            </Button>
            <Button className={myStyles.viewButton} onClick={() => setTabViewIndex(2)}>
              <div className={classNames(myStyles.viewButtonContent, tabViewIndex === 2 && myStyles.viewButtonContentSelected)}>
                <span className="material-icons">
                  event
                </span>
                <div>
                  Scheduled
                </div>
              </div>
            </Button>
          </div>

          {tabViewIndex === 0 && (
            getParticipantAssessmentsOverview()
          )}

          {tabViewIndex === 1 && (
            getParticipantAssessmentsCompleted()
          )}

          {tabViewIndex === 2 && (
            getParticipantAssessmentsSchedules()
          )}
        </div>

      </div>
    </div>
  )
}
